
import { RESOURCE_TYPE_PROFILES, NAV_TO_PROFILE_LIST } from '@/resources-def/profiles-comp';
import { useCreate, useOne, useInvalidate, useUpdate } from "@refinedev/core";
import { BetaSchemaForm } from "@ant-design/pro-components";
import { Button, Modal, Popconfirm, Spin, Steps } from "antd";
import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { CreationWrapper } from "@/layouts/CreationWrapper";
import { deriveUIDefaultsFromServerFormat } from '@/components/instance/config/ServerToClientConverter';
import { genInstanceProfileForServer } from "@/components/instance/config/ClientToServerConverter";
import { useParams } from "react-router-dom";
import { useResourceNavigate } from "@/hooks/useResourceNavigate";
import { useResourceContext } from "@/layouts/ResourceContext";
import { useProfileWizardConfig } from "@/hooks/config/useProfileWizardConfig";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import { t } from 'i18next';

export const Wizard = ({title, description, submitIdx = -1, profileObj={}, action="create" }) => {
  const formRef = useRef();
  const formMapRef = useRef();
  const [loading, setLoading] = useState(false);
  
  const { dispatch, rootState} = useResourceContext();

  const invalidate = useInvalidate();

  const navigate = useResourceNavigate();

  const { t } = useTranslation();
  const templateRef = useRef(profileObj.profile||{});
  const initializedRef = useRef({});
  const [currentStep, setCurrentStep] = useState(0);


  const onValuesChange = (changedValues, allValues) => {
    if (changedValues.hasOwnProperty("template")) {
      initializedRef.current = {};
    }
  };

  const onValuesChangeCallback = useCallback((changedValues, allValues) => {
    onValuesChange(changedValues, allValues);
  }, []);

  const { steps, columns, template:templateFun } = useProfileWizardConfig({
    action,
    formRef,
    onValuesChangeCallback,
  });

  const [ss, setSS] = useState([...steps]);

  useEffect(() => {
    
    if (initializedRef.current[currentStep] != true) {
      initializedRef.current[currentStep] = true;
      if(currentStep == 0 && action != "create"){
        formRef.current.setFieldsValue(profileObj);
      } else {
        const templateValue = templateFun[currentStep](templateRef.current);
        formRef.current.setFieldsValue(templateValue);
      }
    }
  }, [currentStep]);

  const onCurrentChange = (current) => {
    if (current === 1 && initializedRef.current[current] != true) {
      if(action == "create"){
        const template = formRef.current.getFieldValue("template-define");
        const dv = deriveUIDefaultsFromServerFormat(template.value.profile);
        templateRef.current = dv;
      } else {
          templateRef.current = profileObj.profile;
      }
    }
    setCurrentStep(current);
    if(steps.length != ss.length)
      setSS([...steps]);
  };

  const { mutate: create } = useCreate({
    resource:RESOURCE_TYPE_PROFILES,
    successNotification: (data, values, resource) => {
      setLoading(false);
      invalidate({
        resource,
        invalidates: ["list"],
      });

      let name = formMapRef.current[0].current.getFieldValue("name");
      if (data?.result?.status === "OK") {

        rootState?.eks?.data["instance-profiles"].push(name);
        
        dispatch({
          type: "SELECT_RESOURCE",
          payload: name,
        });
        navigate({
          resource:NAV_TO_PROFILE_LIST,
          key: name,
          action:"list"
        });

        return {
          message: t(`settings.actionResult.create.success`, {
            resource:"profile",
            id: name,
          }),
          type: "success",
        };
      } else {
        return {
          message: data?.result?.message,
          type: "error",
        };
      }
    },
    errorNotification: (data, values, resource) => {
      setLoading(false);
      return {
        message: t(`settings.actionResult.create.failed`, {
          resource:"profile",
          id: name,
        }),
        type: "error",
      };
    },
  });
  const { mutate: updateMutate } = useUpdate({
    resource: RESOURCE_TYPE_PROFILES,
    successNotification: (data, values, resource) => {
      setLoading(false);
      invalidate({
        resource,
        invalidates: ["list"],
      });
      navigate({
        resource: NAV_TO_PROFILE_LIST,
        action: "list",
      });
      return {
        message: t(`settings.actionResult.edit.success`, {
          resource: "profile",
          id:profileObj?.name,
        }),
        type: "success",
      };
    },
    errorNotification: (data, values, resource) => {
      setLoading(false);
      return {
        message: t(`settings.actionResult.edit.failed`, {
          resource: "profile",
          id:profileObj?.name,
        }),
        type: "error",
      };
    },
  });
  const handleSubmit = async (step) => {
    try {
      await formRef.current?.validateFields();
      console.log(formMapRef);
      if(formMapRef && formMapRef.current) {
        const values =  formMapRef.current.reduce((acc, obj, index) => {
          let value;
          if(index <= step) {
            value = obj.current.getFieldsValue();
          } else {
            value = templateFun[index](templateRef.current);
          }
          return {...acc, ...value};
        }, {})
  
        function filterEmptyValues(obj) {
          const filteredObj = {};
          
          for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
              const value = obj[key];
              if (value !== null && value !== undefined && value !== '') {
                filteredObj[key] = value;
              }
            }
          }
          
          return filteredObj;
        }
        onFinish(filterEmptyValues(values));
      }
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    const config = genInstanceProfileForServer({
      ...values,
      type: "silo",
      enableDruid: true
    });
    let desc = formMapRef.current[0].current.getFieldValue("desc");
    let data = {desc:desc||"", profile:config.profile}
    if(action == "create"){
      data.name = config.name;
      create({
        values: data,
      });
    } else {
      updateMutate({
        id:profileObj.name,
        values:data
      });
    }
   console.log(data);
  };

  const onCancel = () => {
    navigate({ resource: NAV_TO_PROFILE_LIST, action: "list" });
  };

  const submitter = submitIdx != -1 ? {

    render: (props) => {
      if (props.step === 0) {
        return [
          <Button type="primary" onClick={() => props.onSubmit?.()}>
            Next
          </Button>,
          <Button onClick={() => onCancel()}>
            Cancel
          </Button>
        ];
      }

      if (props.step > 0 && props.step < steps.length - 1) {
        return [
          <Button key="pre" onClick={() => props.onPre?.()}>
            Previous
          </Button>,
          <Button
            key="next"
            onClick={() => props.onSubmit?.()}
          >
            Next
          </Button>,
          props.step >= submitIdx && <Popconfirm
          title={action == "create"?"Create the profile":"Save the profile"}
          onConfirm={() => handleSubmit(props.step)}
          description={action == "create"?"Are you sure you want to create the profile with default configuration for left steps?":"Are you sure you want to save the profile?"}
          icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
        ><Button       
            key="submit"
            type="primary"
          >
            Submit
          </Button></Popconfirm>,
          <Button  onClick={() => onCancel()}>
            Cancel
          </Button>
        ];
      }

      return [
        <Button key="gotoTwo" onClick={() => props.onPre?.()}>
          {'<'} Previous
        </Button>,
        <Button
          type="primary"
          key="submit"
          onClick={() => props.onSubmit?.()}
        >
          Submit
        </Button>,
        <Button onClick={() => onCancel()}>
          Cancel
        </Button>
      ];
    },
  } : {};

  return (
    <Spin spinning={loading}>
      <CreationWrapper title={t(title)} desc={description} footer={<></>}>
        <Steps items={steps} current={currentStep}/>
        <BetaSchemaForm
          layoutType="StepsForm"
          onCurrentChange={onCurrentChange}
          formRef={formRef}
          formMapRef={formMapRef}
          onFinish={onFinish}
          steps={ss}
          stepsRender={(steps, dom) => (<div></div>)}
          columns={columns}
          formProps={{
            onValuesChange: onValuesChange,
            // initialValues: formValues,
          }}
          submitter={submitter}
        />
      </CreationWrapper>
    </Spin>
  );
};

export const CreateProfilePage = () => {
  return (
      <Editor desc={t("settings.profiles.actionDesc.create")}/>
  )
}

export const EditProfilePage = () => {
  const { state } = useResourceContext();
  const selected = state.selectedResources[0];
  const { data, isLoading } = useOne({
    resource: RESOURCE_TYPE_PROFILES,
    id: selected?.name,
    skip: !selected,
  });
  const instance = data || selected;
  const tmp = deriveUIDefaultsFromServerFormat(instance.profile||{});
  return (
    <Editor isCreate={false} profileObj={{name:instance.name, desc:instance.desc,profile:tmp}} 
      desc={t("settings.profiles.actionDesc.edit")}/>
  )
}

const Editor = ({isCreate=true, profileObj={}, desc=""}) => {
  return (<Wizard
        title={isCreate?"settings.profiles.actions.create":"settings.profiles.actions.edit"}
        description={desc}
        submitIdx={0}
        profileObj={profileObj}
        action={isCreate?"create":"edit"}
      />);
}